import { useCallback, useContext, useMemo } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { HeaderNav, TypeEditableTitle, Status } from '@components/layout/headerNav'
import { TitleWithBadge } from '@components/titleWithBadge'
import { submissionContext } from '@contexts'
import { submissionStatusColors } from '@global/constants'
import { t } from '@lingui/core/macro'
import { Submission } from '@global/types'

interface Props {
  title: string | null
  status?: Status | null
  onBlurCallback?: (value: string) => void
  onFocusCallback?: () => void
  editableTitleCallback?: TypeEditableTitle | null
  editableTitleRequired?: boolean
  titleValidationError?: boolean
  templateId?: string
  hasCampaignHealth?: boolean
}

// TODO: delete this when app/contexts/submission-context.jsx is converted to TS
interface OverriddenSubmissionContext {
  byId: Partial<Record<string, Submission>>
}

/**
 * The header and navigation component for Submissions;
 * including the selective edit header on path /codes/details/:id/selective.
 * @param props React Props
 * @param {string} props.title The submission name.
 * @param {Status} props.status The UI pill status for the flow stage of the submission.
 * @param {TypeEditableTitle} props.editableTitleCallback The onChange event function that handles submission name changes.
 * @param {boolean} props.editableTitleRequired Submission name is required to save data.
 * @param {boolean} props.titleValidationError Is there an error with the current submission name to be displayed in the UI.
 * @returns {ReactNode}
 */
interface SubmissionsDetailsHeaderRouterParams {
  id: string
}

export const SubmissionsDetailsHeader = ({
  title = null,
  status = null,
  editableTitleCallback = null,
  editableTitleRequired = false,
  titleValidationError = false,
  onBlurCallback = () => void {},
  onFocusCallback = () => void {},
  templateId,
  hasCampaignHealth,
}: Props) => {
  const { byId } = useContext<OverriddenSubmissionContext>(
    submissionContext as unknown as React.Context<OverriddenSubmissionContext>,
  ) // TODO: fix this when app/contexts/submission-context.jsx is converted to TS

  const isSubmissionPage = useRouteMatch<SubmissionsDetailsHeaderRouterParams>('/codes/details/:id')
  const isSelectivePath = Boolean(location.pathname.includes('selective'))
  const id = isSubmissionPage?.params.id ?? null
  const submission = useMemo(() => (id !== null ? (byId[id] ?? null) : null), [id, byId])

  const isSelected = useCallback(
    function isSelected(name) {
      const currPathParts = location.pathname.split('/').filter(c => c)
      const currentPageName = currPathParts[currPathParts.length - 1]

      return currentPageName === name
    },
    [location.pathname],
  )

  const headerLinks = [
    { label: t`Data`, path: 'data', selected: isSelected('data') },
    {
      label: t`History`,
      path: 'history',
      selected: isSelected('history'),
      badge: () => (
        <TitleWithBadge
          isLoading={false}
          dotOnly={true}
          color={submission !== null ? submissionStatusColors[submission.status] : ''}
          title={t`History`}
        />
      ),
    },
    ...(templateId && hasCampaignHealth
      ? [
          {
            label: t`Campaign health`,
            path: `/template/${String(templateId)}/campaign-health`,
            selected: isSelected('campaign-health'),
          },
        ]
      : []),
  ]

  return (
    <HeaderNav
      title={title}
      status={status}
      links={isSelectivePath ? [] : headerLinks}
      editableTitleCallback={editableTitleCallback}
      onBlurCallback={onBlurCallback}
      onFocusCallback={onFocusCallback}
      editableDefaultTitle={t`Untitled Submission`}
      editableTitleRequired={editableTitleRequired}
      titleValidationError={titleValidationError}
    />
  )
}
